import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectHierarchyFilters } from 'app/pages/Home/slice/selectors';
import type { GroupedByAudience } from './useMustWinSwimLanes';

/**
 * Sorts grouped audiences and their strategies using only audienceStrategyMappings:
 * - Audiences: same order as keys in audienceStrategyMappings. Audiences not in the mapping = at end (then by name).
 * - Strategies: within each audience, same order as audienceStrategyMappings[audience] array. Strategies not in the list = at end (then by name).
 */
function applyOrder(
  grouped: GroupedByAudience[],
  orderConfig: Record<string, string[]>
): GroupedByAudience[] {
  const audienceOrderKeys = Object.keys(orderConfig);
  const audienceOrderMap: Map<string, number> = new Map<string, number>();
  audienceOrderKeys.forEach((key, idx) => audienceOrderMap.set(key, idx));

  const sortedGroups = [...grouped].sort((a, b) => {
    const aIdx =
      audienceOrderMap.get(a.audienceName) ?? audienceOrderKeys.length;
    const bIdx =
      audienceOrderMap.get(b.audienceName) ?? audienceOrderKeys.length;
    if (aIdx !== bIdx) return aIdx - bIdx;
    return a.audienceName.localeCompare(b.audienceName);
  });

  return sortedGroups.map(group => {
    const strategyOrder = orderConfig[group.audienceName] ?? [];
    const strategyOrderMap: Map<string, number> = new Map<string, number>();
    strategyOrder.forEach((name, idx) => strategyOrderMap.set(name, idx));

    const sortedStrategies = [...group.strategies].sort((a, b) => {
      const aIdx = strategyOrderMap.get(a.strategyName) ?? strategyOrder.length;
      const bIdx = strategyOrderMap.get(b.strategyName) ?? strategyOrder.length;
      if (aIdx !== bIdx) return aIdx - bIdx;
      return a.strategyName.localeCompare(b.strategyName);
    });

    return { ...group, strategies: sortedStrategies };
  });
}

/**
 * Returns groupedByAudience ordered by hierarchyFilters.audienceStrategyMappings only.
 * - Audience order = key order in audienceStrategyMappings.
 * - Strategy order per audience = array order in audienceStrategyMappings[audience].
 * - Audiences/strategies not in the mapping appear at the end.
 */
export function useOrderedGroupedByAudience(
  groupedByAudience: GroupedByAudience[]
): GroupedByAudience[] {
  const hierarchyFilters = useSelector(selectHierarchyFilters);
  const audienceStrategyMappings =
    hierarchyFilters?.audienceStrategyMappings ?? {};

  return useMemo(() => {
    if (
      typeof audienceStrategyMappings !== 'object' ||
      Object.keys(audienceStrategyMappings).length === 0
    ) {
      return groupedByAudience;
    }
    return applyOrder(groupedByAudience, audienceStrategyMappings);
  }, [groupedByAudience, hierarchyFilters?.audienceStrategyMappings]);
}
